/**
 * 时间和文件大小格式化工具函数
 */

const pad = num => num.toString().padStart(2, '0')

/**
 * 紧凑时间格式: MM:SS (带前导零)
 * 用于: AudioPlayer 播放时间显示
 */
export function formatTimeCompact(seconds) {
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${pad(m)}:${pad(s)}`
}

/**
 * 完整时间格式: HH:MM:SS 或 MM:SS (自动判断是否需要小时)
 * 用于: SubtitleDisplay 字幕时间戳
 */
export function formatTimeHHMMSS(seconds) {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = Math.floor(seconds % 60)
  if (hours > 0) {
    return `${pad(hours)}:${pad(minutes)}:${pad(secs)}`
  }
  return `${pad(minutes)}:${pad(secs)}`
}

/**
 * SRT 字幕时间格式: HH:MM:SS,mmm
 * 用于: ExportPanel 导出 SRT 文件
 */
export function formatTimeSRT(seconds) {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = Math.floor(seconds % 60)
  const ms = Math.floor((seconds % 1) * 1000)
  return `${pad(hours)}:${pad(minutes)}:${pad(secs)},${ms.toString().padStart(3, '0')}`
}

/**
 * 时长格式: M:SS (不带分钟前导零)
 * 用于: RecentFiles, AudioUploader 文件列表显示
 * @param {number} duration - 秒数
 * @param {string} fallback - duration 为空时的回退值，默认 '--:--'
 */
export function formatDuration(duration, fallback = '--:--') {
  if (!duration) return fallback
  const minutes = Math.floor(duration / 60)
  const seconds = Math.floor(duration % 60)
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

/**
 * 文件大小格式化: 自动选择 B/KB/MB/GB 单位
 * 用于: AudioUploader, TextToSpeechUploader 文件大小显示
 */
export function formatFileSize(bytes) {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

/**
 * 紧凑数字: 中文 1941万，英文 19.4M
 * 用于: 素材库 YouTube 订阅数、播放量
 */
export function formatCompactNumber(value, locale) {
  return new Intl.NumberFormat(locale, { notation: 'compact', maximumFractionDigits: 1 }).format(value)
}

const RELATIVE_UNITS = [
  ['year', 365 * 86400],
  ['month', 30 * 86400],
  ['week', 7 * 86400],
  ['day', 86400]
]

/**
 * 相对日期: 3 年前 / 3 years ago
 * 用于: 素材库 YouTube 发布日期
 */
export function formatRelativeDate(isoDate, locale) {
  const seconds = (Date.now() - new Date(isoDate).getTime()) / 1000
  const [unit, size] = RELATIVE_UNITS.find(([, size]) => seconds >= size) || RELATIVE_UNITS[RELATIVE_UNITS.length - 1]
  return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(-Math.floor(seconds / size), unit)
}
